import {
  AlignItems,
  Box,
  Button,
  ButtonColorVariant,
  Color,
  Display,
  GridDisplay,
  JustifyContent,
  Spinner,
} from '@gousto-internal/citrus-react'
import React from 'react'
import { isSkipOrder } from '../orderTileState'

type OrderTileActionsProps = {
  helpCta: string | null
  isManageDeliveryExpanded: boolean
  orderState: string
  orderStateTitle: string
  isRestoreOrderDisabled: boolean
  onChooseRecipesClick: () => void
  onGetHelpClick: () => void
  onManageDeliveryClick: () => void
  onRestoreOrderClick: () => void
}

export const OrderTileActions = ({
  helpCta,
  isManageDeliveryExpanded,
  isRestoreOrderDisabled,
  orderState,
  orderStateTitle,
  onChooseRecipesClick,
  onGetHelpClick,
  onManageDeliveryClick,
  onRestoreOrderClick,
}: OrderTileActionsProps) => {
  const recipeActionConfig =
    orderState === 'choose'
      ? {
          colorVariant: ButtonColorVariant.Primary,
          label: 'Choose recipes',
          onClick: onChooseRecipesClick,
          disabled: false,
        }
      : orderState === 'edit'
      ? {
          colorVariant: ButtonColorVariant.Secondary,
          label: 'Add or swap recipes',
          onClick: onChooseRecipesClick,
          disabled: false,
        }
      : isSkipOrder({
          state: orderState,
          state_title: orderStateTitle,
        })
      ? {
          colorVariant: ButtonColorVariant.Primary,
          label: 'Restore order',
          onClick: onRestoreOrderClick,
          disabled: isRestoreOrderDisabled,
        }
      : null
  const shouldShowRecipeActionButton = Boolean(recipeActionConfig)
  const helpButtonLabel = helpCta ?? (orderState === 'dispatched' ? 'Get help' : null)
  const shouldShowHelpButton = Boolean(helpButtonLabel) && !shouldShowRecipeActionButton
  const manageDeliveryButtonVariant =
    orderState === 'premenu' ? ButtonColorVariant.Secondary : ButtonColorVariant.Tertiary
  const shouldShowRestoreOrderLoading =
    recipeActionConfig?.label === 'Restore order' && isRestoreOrderDisabled

  return (
    <Box
      display={GridDisplay.Grid}
      gridTemplateColumns={['1fr', '1fr', 'repeat(2, minmax(0, 1fr))']}
      gap={4}
    >
      {recipeActionConfig ? (
        <Button
          colorVariant={recipeActionConfig.colorVariant}
          disabled={recipeActionConfig.disabled}
          onClick={recipeActionConfig.onClick}
          width="100%"
          data-testid="recipe-action-button"
          data-color-variant={recipeActionConfig.colorVariant}
        >
          {shouldShowRestoreOrderLoading ? (
            <Box
              display={Display.Flex}
              alignItems={AlignItems.Center}
              justifyContent={JustifyContent.Center}
              gap={2}
            >
              <Spinner
                aria-hidden="true"
                color={Color.White}
                data-testid="restore-order-spinner"
                size={6}
              />
              <span>Restoring order</span>
            </Box>
          ) : (
            recipeActionConfig.label
          )}
        </Button>
      ) : null}
      {shouldShowHelpButton ? (
        <Button colorVariant={ButtonColorVariant.Secondary} onClick={onGetHelpClick} width="100%">
          {helpButtonLabel}
        </Button>
      ) : null}
      <Box display={['none', 'none', 'block']}>
        <Button
          colorVariant={manageDeliveryButtonVariant}
          onClick={onManageDeliveryClick}
          width="100%"
          afterIcon={isManageDeliveryExpanded ? 'chevron_up' : 'chevron_down'}
          data-testid="manage-delivery-button"
          data-color-variant={manageDeliveryButtonVariant}
        >
          Manage delivery
        </Button>
      </Box>
    </Box>
  )
}
